// LearnCore Frontend - 主题切换组件

import { useTheme } from '../hooks/useTheme';

interface ThemeToggleProps {
  className?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export default function ThemeToggle({
  className = '',
  showLabel = false,
  size = 'md'
}: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return (
          <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        );
      case 'dark':
        return (
          <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        );
      case 'auto':
        return (
          <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const getThemeLabel = () => {
    switch (theme) {
      case 'light':
        return '浅色模式';
      case 'dark':
        return '深色模式';
      case 'auto':
        return '跟随系统';
      default:
        return '未知';
    }
  };

  const getThemeTitle = () => {
    switch (theme) {
      case 'light':
        return '当前：浅色模式，点击切换到深色模式';
      case 'dark':
        return '当前：深色模式，点击切换到跟随系统';
      case 'auto':
        return '当前：跟随系统，点击切换到浅色模式';
      default:
        return '点击切换主题';
    }
  };

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const buttonClasses = `
    p-2 rounded-lg transition-all duration-200
    hover:bg-gray-100 dark:hover:bg-gray-800
    text-gray-600 dark:text-gray-400
    hover:text-gray-900 dark:hover:text-gray-100
    focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
    flex items-center justify-center
    ${className}
  `;

  return (
    <div className="flex items-center space-x-2">
      <button
        className={buttonClasses}
        onClick={toggleTheme}
        title={getThemeTitle()}
        aria-label={`主题切换，当前${getThemeLabel()}`}
      >
        <span className={sizeClasses[size]}>
          {getThemeIcon()}
        </span>
      </button>

      {showLabel && (
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {getThemeLabel()}
        </span>
      )}
    </div>
  );
}